import { MGLoader } from '@mg/ui/components/MGLoader';
import { MGStatusIndicator } from '@mg/ui/components/MGStatusIndicator';
import { MGTabs } from '@mg/ui/components/MGTabs';
import { fontSize } from '@mg/ui/styles/primitives';
import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { MONO } from '../../styles/code.js';

type DotTone = 'success' | 'neutral' | 'danger' | 'warning';

const INDICATOR: Record<Exclude<DotTone, 'neutral'>, 'success' | 'error' | 'warning'> = {
  success: 'success',
  danger: 'error',
  warning: 'warning',
};

/** The status light in front of a status line; `pulse` while something is live. */
export function StatusDot({ tone, pulse = false }: { tone: DotTone; pulse?: boolean }): ReactNode {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 20,
        height: 20,
        display: 'grid',
        placeItems: 'center',
        flex: 'none',
        ...(pulse && {
          animation: 'ee-pulse 1.6s ease-in-out infinite',
          '@keyframes ee-pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
        }),
      }}
    >
      {tone === 'neutral' ? (
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'text.disabled' }} />
      ) : (
        <MGStatusIndicator status={INDICATOR[tone]} />
      )}
    </Box>
  );
}

type InlineTone = 'success' | 'warning' | 'danger';

const INLINE_COLOR: Record<InlineTone, string> = {
  success: 'success.onContainer',
  warning: 'warning.onContainer',
  danger: 'error.main',
};

const INLINE_DOT: Record<InlineTone, string> = {
  success: 'success.main',
  warning: 'warning.main',
  danger: 'error.main',
};

/** A dot and a few words, coloured by tone: `● Valid JSON`. */
export function InlineStatus({ tone, children }: { tone: InlineTone; children: ReactNode }): ReactNode {
  return (
    <Typography
      variant="extraSmall"
      component="span"
      role="status"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: INLINE_COLOR[tone] }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: INLINE_DOT[tone] }} />
      {children}
    </Typography>
  );
}

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

/** Two or three mutually exclusive choices, as the design system's sunken pill tabs. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: SegmentedControlProps<T>): ReactNode {
  return (
    <Box sx={{ display: 'inline-flex' }}>
      <MGTabs<T> tabs={options} value={value} onChange={onChange} variant="sunken" />
    </Box>
  );
}

export function EmptyState({ children }: { children: ReactNode }): ReactNode {
  return (
    <Typography
      variant="small"
      component="div"
      sx={{ px: 3, py: 6, textAlign: 'center', color: 'text.muted' }}
    >
      {children}
    </Typography>
  );
}

interface LoadingProps {
  /** The whole viewport, for the moments before there is a page at all. */
  full?: boolean;
  /** The box's height otherwise; a panel body or an editor's footprint. */
  height?: number;
  /** The smaller spinner, for spaces narrower than a page. */
  small?: boolean;
}

/** A centred spinner announcing itself: routes while their loader runs, panels while they fetch. */
export function Loading({ full = false, height = 240, small = false }: LoadingProps): ReactNode {
  return (
    <Box
      sx={{ height: full ? '100vh' : height, display: 'grid', placeItems: 'center' }}
      role="status"
      aria-label="Loading"
    >
      <MGLoader size={small ? '40px' : '56px'} />
    </Box>
  );
}

interface StatsProps {
  items: { value: ReactNode; label: string }[];
}

/** `27 events · 14 current · 3 streams` — a quiet count strip beside a title. */
export function Stats({ items }: StatsProps): ReactNode {
  return (
    <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap' }}>
      {items.map((item) => (
        <Typography key={item.label} variant="small" sx={{ color: 'text.muted' }}>
          <Box component="b" sx={{ color: 'text.strong', fontWeight: 'bold' }}>
            {item.value}
          </Box>{' '}
          {item.label}
        </Typography>
      ))}
    </Stack>
  );
}

/** The explanatory paragraph under a page header. */
export function Intro({ children }: { children: ReactNode }): ReactNode {
  return (
    <Typography variant="small" component="p" sx={{ color: 'text.muted', maxWidth: 820, textWrap: 'pretty' }}>
      {children}
    </Typography>
  );
}

/** Inline code in running text — a subject, a header name, an environment variable. */
export function Mono({ children }: { children: ReactNode }): ReactNode {
  return (
    <Box component="code" sx={{ fontFamily: MONO, fontSize: fontSize.extraSmall, color: 'text.strong' }}>
      {children}
    </Box>
  );
}
